import React, { useEffect, useState } from "react"
import { withRouter } from "react-router-dom"
import MetaTags from "react-meta-tags"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import {
  STATUS_META,
  BONUS_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  TIMEZONE_OPTIONS,
  addCampaign,
  getCampaignById,
  mapCampaignApiResponse,
  filterOutExcludedCountries,
} from "./data"
import {
  getBonusCampaignLookupsCall,
  getAffiliatesCall,
  createBonusCampaignCall,
  updateBonusCampaignCall,
} from "helpers/repository"
import ConfirmDialog from "./ConfirmDialog"
import StatusBadge from "./StatusBadge"
import { useCanManageAffiliateBonuses } from "./permissions"
import { sitename } from "../../../constants/constants"

const STATUS_ACTIONS = [
  { value: "Draft", label: "Draft", icon: "mdi-file-document-edit-outline" },
  { value: "Active", label: "Activate", icon: "mdi-play" },
  { value: "Paused", label: "Pause", icon: "mdi-pause" },
  { value: "Expired", label: "Expired", icon: "mdi-clock-outline" },
  { value: "Archived", label: "Archive", icon: "mdi-archive-outline" },
]

const emptyForm = {
  name: "",
  subtitle: "",
  description: "",
  status: "Draft",
  bonusType: BONUS_TYPE_OPTIONS[0].value,
  currencyCode: "NGN",
  amountValue: "",
  startDate: "",
  endDate: "",
  timezone: TIMEZONE_OPTIONS[0].value,
  maxClaims: "",
  wageringRequirement: "x3",
  expiryDays: 14,
  minDeposit: "",
  maxBonusAmount: "",
  eligibleCountries: [],
  eligibleBrands: [],
  eligibleAffiliates: [],
  toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
}

const validate = f => {
  const errors = {}
  if (!f.subtitle.trim()) errors.subtitle = "Campaign name is required."
  if (!f.name.trim()) errors.name = "Bonus name is required."
  const amt = parseFloat(f.amountValue)
  if (!f.amountValue || isNaN(amt) || amt <= 0) errors.amountValue = "Amount must be greater than 0."
  if (!f.startDate) errors.startDate = "Start date is required."
  if (!f.endDate) errors.endDate = "End date is required."
  if (f.startDate && f.endDate && new Date(f.endDate) <= new Date(f.startDate)) {
    errors.endDate = "End date must be after the start date."
  }
  return errors
}

const ChipToggle = ({ options, selected, onToggle, disabled, emptyLabel, searchPlaceholder }) => {
  const [search, setSearch] = useState("")

  if (options.length === 0) {
    return <div className="text-muted font-size-12">{emptyLabel ?? "No options available."}</div>
  }

  const visible = searchPlaceholder
    ? options.filter(opt => opt.name.toLowerCase().includes(search.trim().toLowerCase()))
    : options

  return (
    <div>
      {searchPlaceholder && (
        <div className="search-box mb-2 w-100">
          <div className="position-relative">
            <Input type="text" placeholder={searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} />
            <i className="bx bx-search-alt search-icon" />
          </div>
        </div>
      )}
      <div className="d-flex flex-wrap gap-2">
        {visible.length === 0 ? (
          <div className="text-muted font-size-12">No matches.</div>
        ) : (
          visible.map(opt => {
            const active = selected.includes(opt.id)
            return (
              <button
                type="button"
                key={opt.id}
                disabled={disabled}
                className={`btn btn-sm ab-chip ${active ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => onToggle(opt.id)}
              >
                {active && <i className="mdi mdi-check me-1" />}
                {opt.name}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

const Create = props => {
  const urlParams = new URLSearchParams(props.location.search)
  const id = urlParams.get("id")
  const canEdit = useCanManageAffiliateBonuses()
  const [form, setForm] = useState(emptyForm)
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [confirm, setConfirm] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const [countryOptions, setCountryOptions] = useState([])
  const [brandOptions, setBrandOptions] = useState([])
  const [affiliateOptions, setAffiliateOptions] = useState([])
  const [lookupsLoading, setLookupsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([getBonusCampaignLookupsCall(), getAffiliatesCall({ per_page: 100 })])
      .then(([lookupsRes, affiliatesRes]) => {
        if (cancelled) return
        const lookups = lookupsRes?.data?.data ?? {}
        setCountryOptions(filterOutExcludedCountries(lookups.countries ?? []))
        setBrandOptions(lookups.brands ?? [])
        const affiliateRows =
          affiliatesRes?.data?.data?.data ?? affiliatesRes?.data?.data ?? affiliatesRes?.data ?? []
        setAffiliateOptions(
          (Array.isArray(affiliateRows) ? affiliateRows : []).map(a => ({
            id: a.id,
            // /admin/affiliates has no display-name field — affiliates are
            // identified by their referral code, not a company/person name.
            name: a.username ?? a.referral_code ?? a.phone_number ?? `Affiliate #${a.id}`,
          }))
        )
      })
      .catch(err => {
        console.error(err)
        toastr.error("Could not load countries, brands, or affiliates for this form.")
      })
      .finally(() => {
        if (!cancelled) setLookupsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!id) return
    // Prefer the row handed over via router state (from Table.js/View.js) since
    // it reflects the real API — there's no GET-by-id endpoint yet, so it's the
    // only place to get a real campaign's current values. Falls back to the
    // local mock store for campaigns created against the mock-only flow.
    const existing = props.location.state?.campaign ?? getCampaignById(id)
    if (!existing) return
    const namesToIds = (names, options) =>
      (names ?? []).map(name => options.find(o => o.name === name)?.id).filter(v => v !== undefined)
    setForm({
      ...emptyForm,
      ...existing,
      amountValue: (existing.amount || "").replace(/[^0-9.]/g, ""),
      bonusType: BONUS_TYPE_OPTIONS.find(o => o.label === existing.bonusType)?.value ?? emptyForm.bonusType,
      timezone: TIMEZONE_OPTIONS.find(o => o.label === existing.timezone)?.value ?? emptyForm.timezone,
      maxClaims: existing.maxClaims ?? "",
      eligibleCountries: namesToIds(existing.eligibleCountries, countryOptions),
      eligibleBrands: namesToIds(existing.eligibleBrands, brandOptions),
      eligibleAffiliates: namesToIds(existing.eligibleAffiliates, affiliateOptions),
      toggles: { ...emptyForm.toggles, ...(existing.toggles ?? {}) },
    })
  }, [id, countryOptions, brandOptions, affiliateOptions])

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }))
  const setToggle = key => setForm(prev => ({ ...prev, toggles: { ...prev.toggles, [key]: !prev.toggles[key] } }))
  const toggleArrayValue = (field, value) =>
    setForm(prev => {
      const set = new Set(prev[field])
      if (set.has(value)) set.delete(value)
      else set.add(value)
      return { ...prev, [field]: [...set] }
    })

  const closeConfirm = () => setConfirm(null)

  const handleStatusAction = value => {
    if (value === "Archived") {
      setConfirm({
        title: "Archive this campaign?",
        body: "The campaign will be hidden from the default list and stop awarding bonuses. It stays available for reporting.",
        confirmLabel: "Archive",
        onConfirm: () => {
          setField("status", "Archived")
          closeConfirm()
        },
      })
    } else if (value === "Paused" && form.status === "Active") {
      setConfirm({
        title: "Pause this campaign?",
        body: "New registrations will stop receiving this bonus until you resume it.",
        confirmLabel: "Pause",
        onConfirm: () => {
          setField("status", "Paused")
          closeConfirm()
        },
      })
    } else if (value === "Expired") {
      setConfirm({
        title: "Mark this campaign as expired?",
        body: "The campaign will stop awarding bonuses immediately, as if its end date had already passed.",
        confirmLabel: "Mark Expired",
        onConfirm: () => {
          setField("status", "Expired")
          closeConfirm()
        },
      })
    } else {
      setField("status", value)
    }
  }

  // Sent to POST /admin/bonus-campaigns (create) or POST /admin/bonus-campaigns/:id/update (edit).
  const buildApiPayload = statusOverride => ({
    name: form.subtitle,
    bonus_name: form.name,
    description: form.description,
    trigger_event: "affiliate_registration",
    bonus_type: form.bonusType,
    currency: form.currencyCode,
    amount: Number(form.amountValue) || 0,
    min_deposit: form.minDeposit !== "" ? Number(form.minDeposit) : null,
    max_bonus_amount: form.maxBonusAmount !== "" ? Number(form.maxBonusAmount) : null,
    start_date: form.startDate,
    end_date: form.endDate,
    timezone: form.timezone,
    max_claims: Number(form.maxClaims) || null,
    wagering_requirement_multiplier: parseFloat(String(form.wageringRequirement).replace(/[^0-9.]/g, "")) || 0,
    expiry_days: Number(form.expiryDays) || 0,
    one_per_player: form.toggles.onePerPlayer,
    allow_self_referral: form.toggles.allowSelfReferral,
    status: (statusOverride ?? form.status).toLowerCase(),
    country_ids: form.eligibleCountries,
    brand_ids: form.eligibleBrands,
    affiliate_ids: form.eligibleAffiliates,
  })

  const persistCreate = statusOverride => {
    setSubmitting(true)
    createBonusCampaignCall(buildApiPayload(statusOverride))
      .then(res => {
        const created = res?.data?.data ?? res?.data
        addCampaign(mapCampaignApiResponse(created))
        toastr.success(res?.data?.message || "Campaign created successfully")
        props.history.push("/affiliate/bonus-configuration")
      })
      .catch(err => {
        console.error(err)
        toastr.error(err?.response?.data?.message || "Could not create the campaign. Please try again.")
      })
      .finally(() => setSubmitting(false))
  }

  const persistUpdate = statusOverride => {
    setSubmitting(true)
    updateBonusCampaignCall(id, buildApiPayload(statusOverride))
      .then(res => {
        toastr.success(res?.data?.message || "Campaign updated successfully")
        props.history.push("/affiliate/bonus-configuration")
      })
      .catch(err => {
        console.error(err)
        toastr.error(err?.response?.data?.message || "Could not update the campaign. Please try again.")
      })
      .finally(() => setSubmitting(false))
  }

  const runSubmit = () => {
    setSubmitted(true)
    const validationErrors = validate(form)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return
    if (id) {
      persistUpdate()
    } else {
      persistCreate()
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    runSubmit()
  }

  const handleSaveDraft = () => {
    if (id) {
      persistUpdate("draft")
    } else {
      persistCreate("draft")
    }
  }

  const showErrors = submitted && Object.keys(errors).length > 0
  const invalid = key => submitted && !!errors[key]

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{sitename}</title>
        </MetaTags>
        <style>{`
          .ab-card { border-radius: 10px; overflow: hidden; }
          .btn.ab-chip { border-radius: 8px; }
          .btn.ab-status-btn { border-radius: 8px; }
          .btn.ab-header-btn { border-radius: 8px; }
          .ab-sticky-header { position: sticky; top: 70px; z-index: 10; background: var(--bs-body-bg, #f8f8fb); }
          .ab-back-btn { width: 38px; height: 38px; border-radius: 8px; padding: 0; }
        `}</style>
        <Container fluid>
          <div className="ab-sticky-header d-flex align-items-center justify-content-between flex-wrap gap-3 py-2 mb-3">
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-light ab-back-btn d-inline-flex align-items-center justify-content-center"
                onClick={() => props.history.push("/affiliate/bonus-configuration")}
              >
                <i className="mdi mdi-arrow-left font-size-18" />
              </button>
              <div>
                <h4 className="mb-1 font-size-18">{id ? "Edit Campaign" : "New Campaign"}</h4>
                <p className="text-muted mb-0 font-size-12">
                  {id
                    ? "Update the details for this affiliate bonus campaign."
                    : "Configure a new affiliate registration bonus campaign."}
                </p>
              </div>
            </div>
            {canEdit && (
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  color="light"
                  className="ab-header-btn"
                  disabled={submitting}
                  onClick={() => props.history.push("/affiliate/bonus-configuration")}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  color="outline-primary"
                  className="ab-header-btn"
                  disabled={submitting}
                  onClick={handleSaveDraft}
                >
                  Save as Draft
                </Button>
                <Button type="button" color="primary" className="ab-header-btn" disabled={submitting} onClick={runSubmit}>
                  {submitting && <Spinner size="sm" className="me-1" />}
                  {id ? "Save Changes" : "Create Campaign"}
                </Button>
              </div>
            )}
          </div>

          {!canEdit && (
            <Alert color="info">
              You have read-only access to Affiliate Registration Bonuses. Contact an administrator to make changes.
            </Alert>
          )}

          {showErrors && (
            <Alert color="danger" className="d-flex gap-2 align-items-start">
              <i className="mdi mdi-alert-circle-outline font-size-18 mt-1" />
              <div>
                <div className="fw-bold">
                  Please fix {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? "s" : ""} before continuing
                </div>
                <div className="mt-1">{Object.values(errors).join(" ")}</div>
              </div>
            </Alert>
          )}

          <fieldset disabled={!canEdit}>
            <Form onSubmit={handleSubmit}>
              <Card className="mb-3 ab-card">
                <h5 className="card-header bg-transparent border-bottom text-uppercase">
                  1 · General Information
                </h5>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Label>
                        Campaign Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g. Q3 Affiliate Acquisition"
                        value={form.subtitle}
                        invalid={invalid("subtitle")}
                        onChange={e => setField("subtitle", e.target.value)}
                      />
                      <FormFeedback>{errors.subtitle}</FormFeedback>
                    </Col>
                    <Col md={6}>
                      <Label>
                        Bonus Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g. Affiliate Welcome Boost"
                        value={form.name}
                        invalid={invalid("name")}
                        onChange={e => setField("name", e.target.value)}
                      />
                      <FormFeedback>{errors.name}</FormFeedback>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Label>Campaign Description</Label>
                      <Input
                        type="textarea"
                        rows={2}
                        placeholder="Internal note describing this campaign’s purpose."
                        value={form.description}
                        onChange={e => setField("description", e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Label>Bonus Type</Label>
                      <Input
                        type="select"
                        value={form.bonusType}
                        onChange={e => setField("bonusType", e.target.value)}
                      >
                        {BONUS_TYPE_OPTIONS.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Label>Currency</Label>
                      <Input
                        type="select"
                        value={form.currencyCode}
                        onChange={e => setField("currencyCode", e.target.value)}
                      >
                        {CURRENCY_OPTIONS.map(code => (
                          <option key={code} value={code}>
                            {code}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label>
                        Bonus Amount <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="0.00"
                        value={form.amountValue}
                        invalid={invalid("amountValue")}
                        onChange={e => setField("amountValue", e.target.value)}
                        onKeyDown={e => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                      />
                      <FormFeedback>{errors.amountValue}</FormFeedback>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className="mb-3 ab-card">
                <h5 className="card-header bg-transparent border-bottom text-uppercase">
                  2 · Eligibility Rules
                </h5>
                <CardBody>
                  {lookupsLoading && (
                    <div className="text-muted font-size-12 mb-3">
                      <i className="mdi mdi-loading mdi-spin me-1" />
                      Loading countries, brands, and affiliates…
                    </div>
                  )}
                  <Row className="mb-3">
                    <Col md={6}>
                      <Label className="d-block mb-2">Eligible Affiliates</Label>
                      <ChipToggle
                        options={affiliateOptions}
                        selected={form.eligibleAffiliates}
                        onToggle={v => toggleArrayValue("eligibleAffiliates", v)}
                        disabled={!canEdit}
                        emptyLabel="No affiliates found."
                        searchPlaceholder="Search affiliates..."
                      />
                    </Col>
                    <Col md={6}>
                      <Label className="d-block mb-2">Eligible Countries</Label>
                      <ChipToggle
                        options={countryOptions}
                        selected={form.eligibleCountries}
                        onToggle={v => toggleArrayValue("eligibleCountries", v)}
                        disabled={!canEdit}
                        emptyLabel="No countries found."
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Label className="d-block mb-2">Eligible Brands</Label>
                      <ChipToggle
                        options={brandOptions}
                        selected={form.eligibleBrands}
                        onToggle={v => toggleArrayValue("eligibleBrands", v)}
                        disabled={!canEdit}
                        emptyLabel="No brands found."
                      />
                    </Col>
                  </Row>

                  <div className="border-top pt-3">
                    {[
                      { key: "newPlayersOnly", label: "New Players Only", desc: "Award only to first-time registrations." },
                      { key: "onePerPlayer", label: "One Bonus Per Player", desc: "Prevent multiple awards to the same player." },
                      { key: "allowSelfReferral", label: "Allow Self Referrals", desc: "Permit affiliates to refer their own accounts." },
                    ].map(t => (
                      <div key={t.key} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                        <div>
                          <div className="fw-medium">{t.label}</div>
                          <div className="text-muted font-size-12">{t.desc}</div>
                        </div>
                        <div className="form-check form-switch form-switch-lg">
                          <Input
                            type="checkbox"
                            role="switch"
                            disabled={!canEdit}
                            checked={form.toggles[t.key]}
                            onChange={() => setToggle(t.key)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <Row className="mt-3">
                    <Col md={4}>
                      <Label>Maximum Claims (optional)</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Leave blank for unlimited"
                        value={form.maxClaims}
                        onChange={e => setField("maxClaims", e.target.value)}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className="mb-3 ab-card">
                <h5 className="card-header bg-transparent border-bottom text-uppercase">
                  3 · Bonus Conditions
                </h5>
                <CardBody>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Label>Wagering Requirement</Label>
                      <Input
                        type="text"
                        placeholder="e.g. x3"
                        value={form.wageringRequirement}
                        onChange={e => setField("wageringRequirement", e.target.value)}
                      />
                      <div className="text-muted font-size-12 mt-1">
                        Player must wager the bonus this many times before withdrawal.
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Label>Bonus Expiry Duration (days)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.expiryDays}
                        onChange={e => setField("expiryDays", e.target.value)}
                      />
                      <div className="text-muted font-size-12 mt-1">
                        Days after award before the bonus expires if unused.
                      </div>
                    </Col>
                    <Col md={6}>
                      <Label>Minimum Deposit (optional)</Label>
                      <Input
                        type="text"
                        placeholder="0.00"
                        value={form.minDeposit}
                        onChange={e => setField("minDeposit", e.target.value)}
                      />
                      <div className="text-muted font-size-12 mt-1">
                        Minimum deposit required to unlock the bonus.
                      </div>
                    </Col>
                    <Col md={6}>
                      <Label>Maximum Bonus Amount (optional)</Label>
                      <Input
                        type="text"
                        placeholder="0.00"
                        value={form.maxBonusAmount}
                        onChange={e => setField("maxBonusAmount", e.target.value)}
                      />
                      <div className="text-muted font-size-12 mt-1">
                        Caps the total bonus value for match/percentage types.
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className="mb-3 ab-card">
                <h5 className="card-header bg-transparent border-bottom text-uppercase">
                  4 · Campaign Duration
                </h5>
                <CardBody>
                  <Row>
                    <Col md={4} className="mb-3 mb-md-0">
                      <Label>
                        Start Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={form.startDate}
                        invalid={invalid("startDate")}
                        onChange={e => setField("startDate", e.target.value)}
                      />
                      <FormFeedback>{errors.startDate}</FormFeedback>
                    </Col>
                    <Col md={4} className="mb-3 mb-md-0">
                      <Label>
                        End Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={form.endDate}
                        invalid={invalid("endDate")}
                        onChange={e => setField("endDate", e.target.value)}
                      />
                      <FormFeedback>{errors.endDate}</FormFeedback>
                    </Col>
                    <Col md={4}>
                      <Label>Time Zone</Label>
                      <Input
                        type="select"
                        value={form.timezone}
                        onChange={e => setField("timezone", e.target.value)}
                      >
                        {TIMEZONE_OPTIONS.map(tz => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className="mb-3 ab-card">
                <h5 className="card-header bg-transparent border-bottom text-uppercase">
                  5 · Campaign Status
                </h5>
                <CardBody>
                  <Label className="d-block mb-2">Set the lifecycle state for this campaign</Label>
                  <div className="d-flex flex-wrap gap-2">
                    {STATUS_ACTIONS.map(action => {
                      const active = form.status === action.value
                      return (
                        <button
                          type="button"
                          key={action.value}
                          disabled={!canEdit}
                          className={`btn btn-sm ab-status-btn ${active ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => handleStatusAction(action.value)}
                        >
                          <i className={`mdi ${action.icon} me-1`} />
                          {action.label}
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-muted font-size-12 mt-3 mb-0">
                    Pausing or archiving a campaign stops new bonus awards immediately and requires confirmation.
                    Archived campaigns are hidden from the default list but retained for reporting.
                  </p>
                  {STATUS_META[form.status] && (
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <span className="text-muted font-size-12">Current selection:</span>
                      <StatusBadge status={form.status} meta={STATUS_META} />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Form>
          </fieldset>
        </Container>
      </div>
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={closeConfirm}
      />
    </React.Fragment>
  )
}

export const CreateAffiliateBonus = withRouter(Create)
